<script setup lang='ts'>
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { onClickOutside } from '@vueuse/core'

  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import Typography from '@/components/common/Typography.vue'
  import { KsButton } from '@aschehoug/kloss'

  const props = defineProps<{
    isFollowingColorPair?: boolean
    caption?: string
    copyright?: string
  }>()

  const imageCaption = ref(null)
  const isOpen = ref<boolean>(false)

  const { t } = useI18n()

  onClickOutside(imageCaption, () => isOpen.value = false)

  const colorPairClass = computed(() => props.isFollowingColorPair ? 'follow-color-pair' : '')
  // IbexaRichTextField returns this string if rich text is "empty"
  const isCaptionEmpty = computed(() => props.caption === '<?xml version="1.0" encoding="UTF-8"?>\n' || !props.caption)
</script>

<template>
  <div
    v-if="!isCaptionEmpty || !!copyright"
    ref="imageCaption"
  >
    <ks-button
      :aria-label="(isOpen ? 'Lukk' : 'Åpne') + 'bildebeskrivelse'"
      size="small"
      :class="['toggle-button', colorPairClass, { 'open': isOpen }]"
      variant="secondary"
      shape="round"
      :icon-left="isOpen ? 'xmark': 'camera'"
      @click="isOpen = !isOpen"
    />
    <Transition name="fade">
      <div
        v-if="isOpen"
        :class="['credits-section', colorPairClass, { 'copyright-only': isCaptionEmpty }]"
        :aria-label="t('imageCredits')"
      >
        <RichTextRenderer
          v-if="!isCaptionEmpty"
          :rich-text="caption"
        />
        <Typography
          v-if="copyright"
          variant="caption"
        >
          {{ copyright }}
        </Typography>
      </div>
    </Transition>
  </div>
</template>

<style lang='scss' scoped>
  .credits-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-height: 100%;
    min-height: 90px;
    padding: 24px 32px;
    background-color: var(--color-pair-color, var(--color-blue-50));
    color: var(--color-pair-background-color, var(--color-white));
    overflow: auto;
    padding-right: 96px;
    opacity: .85;

    --ks-secondary: var(--color-white);
    --ks-input: var(--color-blue-50);
    --ks-inputhover: var(--color-blue-50);

    :deep(p) {
      font-size: 1rem;
      line-height: 1.5;
      margin: 0;
    }

    :deep(.typography) {
      opacity: 1;
    }

    &.copyright-only {
      flex-direction: row;
      align-items: center;
      gap: 0;
    }

    &:not(&.follow-color-pair) {
      --color-pair-color: initial;
      --color-pair-background-color: initial;
    }

    @include vgs-media-mobile {
      padding: 1.5rem;
      padding-right: 96px;
    }
  }

  .toggle-button {
    position: absolute;
    margin: 28px 32px;
    bottom: 0;
    right: 0;
    z-index: 3;
    opacity: .85;

    &:not(&.follow-color-pair) {
      --color-pair-color: initial;
      --color-pair-background-color: initial;
    }

    &.ks-button {
      background-color: var(--color-pair-color, var(--color-blue-50));
      color: var(--color-pair-background-color, var(--color-white));

      &.open {
        background-color: var(--color-pair-background-color, var(--color-white));
        color: var(--color-pair-color, var(--color-blue-50));
      }
    }
  }

  /* Fade transition from animations.css */
  .fade-enter-active {
    animation: fade-in .4s ease-out;
  }

  .fade-leave-active {
    animation: fade-in .4s reverse ease-in;
  }
</style>
